/** Same channel buckets as SignupFunnelTable. */
function funnelChannelBucket(dimension) {
  if (dimension === 'paid_search' || dimension === 'paid_social') return dimension;
  return 'other';
}

/**
 * Count funnel events for one step in one channel.
 * Rows: `{ pagePath, dimension, count }` from adminProductAnalytics.funnel.
 */
export function funnelStepCount(funnelRows, eventName, channel) {
  return (funnelRows || [])
    .filter(
      (row) => row?.pagePath === eventName && funnelChannelBucket(row.dimension) === channel
    )
    .reduce((sum, row) => sum + (Number(row.count) || 0), 0);
}

/** Sum a funnel step across every channel. */
export function funnelStepTotal(funnelRows, eventName) {
  return (funnelRows || [])
    .filter((row) => row?.pagePath === eventName)
    .reduce((sum, row) => sum + (Number(row.count) || 0), 0);
}

/** Map of step.id → total count for SignupFunnelTable-shaped rows. */
export function funnelStepTotals(steps, funnelRows) {
  const totals = {};
  for (const step of steps || []) {
    if (!step?.id) continue;
    totals[step.id] = funnelStepTotal(funnelRows, step.id);
  }
  return totals;
}

/**
 * Adjacent paid/free funnel pair with the largest count drop (totals, not unique users).
 * Returns null when no step has a previous step with volume.
 */
export function largestAdjacentDrop(steps, funnelRows) {
  const list = steps || [];
  const totals = funnelStepTotals(list, funnelRows);
  let best = null;
  for (const step of list) {
    if (!step?.id || !step.prev) continue;
    const fromCount = totals[step.prev] ?? funnelStepTotal(funnelRows, step.prev);
    const toCount = totals[step.id] ?? 0;
    if (fromCount <= 0) continue;
    const drop = fromCount - toCount;
    if (!best || drop > best.drop) {
      const fromStep = list.find((candidate) => candidate.id === step.prev);
      best = {
        fromId: step.prev,
        toId: step.id,
        fromLabel: fromStep?.label || step.prev,
        toLabel: step.label || step.id,
        fromCount,
        toCount,
        drop,
      };
    }
  }
  return best;
}

function frictionSignals(rows, kind) {
  const merged = new Map();
  for (const row of rows || []) {
    const pagePath = row?.pagePath || '';
    if (!pagePath) continue;
    const count = Number(row.count) || 0;
    if (count <= 0) continue;
    const dimension = row.dimension ?? null;
    const key = `${kind}:${pagePath}:${dimension || ''}`;
    const existing = merged.get(key);
    if (existing) {
      existing.count += count;
    } else {
      merged.set(key, { kind, key, pagePath, dimension, count });
    }
  }
  return [...merged.values()];
}

/** Last path segment counts from adminProductAnalytics.journeys `{ path, count }`. */
export function journeyLastHopCounts(journeys) {
  const totals = new Map();
  for (const row of journeys || []) {
    if (!row?.path) continue;
    const count = Number(row.count) || 0;
    if (count <= 0) continue;
    const segments = String(row.path).split(' → ');
    const pagePath = segments[segments.length - 1];
    if (!pagePath) continue;
    totals.set(pagePath, (totals.get(pagePath) || 0) + count);
  }
  return [...totals.entries()].map(([pagePath, count]) => ({
    kind: 'lastHop',
    key: `lastHop:${pagePath}`,
    pagePath,
    dimension: null,
    count,
  }));
}

/**
 * Rank empty / form / gate rows plus journey last hops. Default top 5.
 * Does not read visit timelines or sessions.
 */
export function whyTheyLeftSignals(sources = {}, opts = {}) {
  const limit = opts.limit == null ? 5 : Number(opts.limit);
  const signals = [
    ...frictionSignals(sources.emptyStates, 'empty'),
    ...frictionSignals(sources.formBlocked, 'form'),
    ...frictionSignals(sources.featureGates, 'gate'),
    ...journeyLastHopCounts(sources.journeys),
  ];
  signals.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.key.localeCompare(b.key);
  });
  if (!Number.isFinite(limit) || limit < 0) return signals;
  return signals.slice(0, limit);
}
